import React, { useCallback, CSSProperties, useMemo } from "react";
import { Option, Key, theme } from "uikit";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import { StyledColumn } from "../../../../../../../common";
import { CardModal, CardModalBase } from "../../../../../../../common/Modal";

const Root = styled(StyledColumn)<{
	modalWidth?: string;
	modalMaxHeight?: string;
}>`
	overflow: hidden;
	border-radius: 5px;
	background-color: ${theme.colors.white};
	width: ${({ modalWidth }) => modalWidth || "500px"};
	max-height: ${({ modalMaxHeight }) => modalMaxHeight || "70vh"};
	display: flex;
	flex-direction: column;
`;

const ContentWrapper = styled.div<{
	modalMinHeight?: string;
}>`
	flex: 1;
	overflow-y: auto;
	padding: 20px;
	min-height: ${({ modalMinHeight }) => modalMinHeight || "600px"};
	gap: 20px;
`;

const SelectModal = <OptionValue, ValueType extends Key>({
	onClose,
	onSubmit,
	children,

	modalWidth,
	modalMaxHeight,
	modalMinHeight,
	...props
}: SelectModal.Props<OptionValue, ValueType>) => {
	const { t } = useTranslation();

	const submitText: string = useMemo(() => t("ok"), [t]);

	const footerOnCancel = useCallback(() => onClose?.(), [onClose]);
	const footerOnSubmit = useCallback(() => onSubmit?.(), [onSubmit]);

	return (
		<CardModal
			{...props}
			onClose={footerOnCancel}
			onSave={footerOnSubmit}
			hiddenButtonClose={true}
			textSave={submitText}
			dimmed={true}
			draggable={false}
		>
			<Root modalWidth={modalWidth} modalMaxHeight={modalMaxHeight}>
				<ContentWrapper modalMinHeight={modalMinHeight}>
					{children}
				</ContentWrapper>
			</Root>
		</CardModal>
	);
};

declare namespace SelectModal {
	type Value<Type extends Key> = Type | undefined;

	interface Props<OptionValue, ValueType extends Key>
		extends Omit<CardModalBase.Props, "onSave"> {
		pullUpItemInsideArray?: boolean;
		value?: Value<ValueType>;
		options?: Option<OptionValue>[];

		onClose: () => void;
		onSubmit: () => void;
		footerJustify?: CSSProperties["justifyContent"];
		dimmed?: boolean;
		canSave?: boolean;
		loading: boolean;
		modalWidth?: string;
		modalMaxHeight?: string;
		modalMinHeight?: string;
	}
}

export default SelectModal;
